import React from 'react';
import { Stack, Typography } from '@mui/material';
import { Autorenew, ChatBubbleOutline, DescriptionOutlined, Phone } from '@mui/icons-material';
import { GlobalInsightsSummary } from '../insightsTypes';
import SummaryCard from './SummaryCard';
import { useDateFormatter } from '@/hooks';

interface InsightsSummaryCardsProps {
    summary: GlobalInsightsSummary;
    isAnalyzing?: boolean;
    // Number of projects chosen in the filter; 0 means "all projects".
    selectedProjectCount?: number;
}

const InsightsSummaryCards: React.FC<InsightsSummaryCardsProps> = ({ summary, isAnalyzing = false, selectedProjectCount = 0 }) => {
    // Hooks must be called inside the component, not at module scope.
    const formatDate = useDateFormatter();

    const callsScope = selectedProjectCount > 0 ? 'Selected projects' : 'Across all projects';

    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <SummaryCard
                label="Projects analyzed"
                value={summary.projectsAnalyzed}
                caption={selectedProjectCount > 0 ? `of ${selectedProjectCount} selected` : 'Projects with activity'}
                tooltip="The count of projects included, scoped to the current project filter selection."
                icon={<DescriptionOutlined />}
                tint="purple"
            />
            <SummaryCard
                label="Calls analyzed"
                value={`${summary.callsAnalyzed}`}
                caption={callsScope}
                tooltip="The total call volume the analysis is based on for the selected time range and project(s)."
                icon={<Phone />}
                tint="green"
            />
            <SummaryCard
                label="Questions analyzed"
                value={`${summary.questionsAnalyzed}`}
                caption="Global prompt questions"
                tooltip="How many global prompt questions were included in this analysis run."
                icon={<ChatBubbleOutline />}
                tint="blue"
            />
            <SummaryCard
                label="Refresh status"
                value={
                    isAnalyzing ? (
                        <Typography variant="h6" color="warning.main" component="span">
                            Analyzing…
                        </Typography>
                    ) : (
                        <Typography variant="h6" color="success.main" component="span">
                            Up to date
                        </Typography>
                    )
                }
                caption={formatDate(summary.lastUpdatedAt)}
                icon={<Autorenew />}
                tint="green"
            />
        </Stack>
    );
};

export default InsightsSummaryCards;
